import React, { useEffect, useState } from "react";
import styles from "../../styles/valuechain/dashboard.module.css";
import LineChart from "../GraphNCharts/LineChart";
import PieChart from "../GraphNCharts/Pie";
import EchartDoughnutChart from "../GraphNCharts/EchartDoughnut";
import { get } from "../../utils/api";
import { generateMonthArray } from "../../utils/formatDate";
import { fixedTons, formatNumberCommaSeparated } from "../../utils/common";
import NoDataFound from "../Masters/NoDataFound";

const generateDataset = (emissionLabels) => {
  const totalValue = emissionLabels.reduce((sum, item) => sum + item.value, 0);
  const data = emissionLabels.map((item) =>
    parseFloat((item.value / totalValue) * 100)
  );
  const backgroundColor = emissionLabels.map((item) => item.color);

  return [
    {
      data,
      backgroundColor,
      borderColor: "#fff",
      borderWidth: 0,
      offset: new Array(emissionLabels.length).fill(15),
    },
  ];
};

const Upstream = ({ valueChainId, fromDate, toDate }) => {
  const [dashboardData, setDashboardData] = useState({
    emissionScope1: null,
    emissionScope2: null,
    emissionScope3: null,
    moduleScope1: null,
    moduleScope2: null,
    moduleScope3: null,
  });

  const apiurls = {
    emissionScope1: `/valueChain/dashboard/scope1/emission-trend?id=${valueChainId}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`,
    moduleScope1: `/valueChain/dashboard/scope1/emission-module?id=${valueChainId}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`,
    emissionScope2: `/valueChain/dashboard/scope2/emission-trend?id=${valueChainId}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`,
    moduleScope2: `/valueChain/dashboard/scope2/emission-module?id=${valueChainId}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`,
    emissionScope3: `/valueChain/dashboard/scope3/emission-trend?id=${valueChainId}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`,
    moduleScope3: `/valueChain/dashboard/scope3/emission-module?id=${valueChainId}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`,
  };

  const fetchData = async () => {
    try {
      const [
        emissionScope1,
        moduleScope1,
        emissionScope2,
        moduleScope2,
        emissionScope3,
        moduleScope3,
      ] = await Promise.all([
        get(apiurls.emissionScope1),
        get(apiurls.moduleScope1),
        get(apiurls.emissionScope2),
        get(apiurls.moduleScope2),
        get(apiurls.emissionScope3),
        get(apiurls.moduleScope3),
      ]);
      setDashboardData({
        emissionScope1: emissionScope1.data,
        moduleScope1: moduleScope1.data,
        emissionScope2: emissionScope2.data,
        moduleScope2: moduleScope2.data,
        emissionScope3: emissionScope3.data,
        moduleScope3: moduleScope3.data,
      });
    } catch (error) {
      console.log("error", error);
      setDashboardData({
        emissionScope1: null,
        emissionScope2: null,
        emissionScope3: null,
        moduleScope1: null,
        moduleScope2: null,
        moduleScope3: null,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [valueChainId, fromDate, toDate]);

  let scope1dataset = [
    dashboardData?.emissionScope1?.stationaryEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Stationary Combustion",
          data:
            dashboardData?.emissionScope1?.stationaryEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(255, 68, 68, 1)",
          backgroundColor: "rgba(255, 68, 68, 0.6)",
          tension: 0.4,
          fill: true,
          pointBorderColor: "#fff",
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope1?.mobileEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Mobile Combustion",
          data:
            dashboardData?.emissionScope1?.mobileEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(255, 187, 51, 1)",
          backgroundColor: "rgba(255, 187, 51, 0.6)",
          tension: 0.3,
          fill: true,
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope1?.fugitiveEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Fugitive Emission",
          data:
            dashboardData?.emissionScope1?.fugitiveEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(0, 102, 231, 1)",
          backgroundColor: "rgba(0, 102, 231, 0.6)",
          tension: 0.3,
          fill: true,
          pointRadius: 0,
        }
      : "",
  ]?.filter(Boolean);

  let scope2dataset = [
    dashboardData?.emissionScope2?.electricityEmissions
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Electricity",
          data:
            dashboardData?.emissionScope2?.electricityEmissions?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(255, 68, 68, 1)",
          backgroundColor: "rgba(255, 68, 68, 0.6)",
          tension: 0.4,
          fill: true,
          pointBorderColor: "#fff",
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope2?.heatEmissions
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Heat",
          data:
            dashboardData?.emissionScope2?.heatEmissions?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(255, 187, 51, 1)",
          backgroundColor: "rgba(255, 187, 51, 0.6)",
          tension: 0.3,
          fill: true,
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope2?.steamEmissions
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Steam",
          data:
            dashboardData?.emissionScope2?.steamEmissions?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(0, 102, 231, 1)",
          backgroundColor: "rgba(0, 102, 231, 0.6)",
          tension: 0.3,
          fill: true,
          pointRadius: 0,
        }
      : "",
  ]?.filter(Boolean);

  let scope3dataset = [
    dashboardData?.emissionScope3?.purchaseGoodsEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Purchased Goods and Services",
          data:
            dashboardData?.emissionScope3?.purchaseGoodsEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "  rgba(255, 187, 51, 1)",
          backgroundColor: "rgba(255, 187, 51, 0.6)",
          tension: 0.4,
          fill: true,
          pointBorderColor: "#fff",
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope3?.transportationEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Upstream Transportation and Distribution",
          data:
            dashboardData?.emissionScope3?.transportationEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(0, 102, 231, 1)",
          backgroundColor: "rgba(0, 102, 231, 0.6)",
          tension: 0.3,
          fill: true,
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope3?.employeeCommuteEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Employee Commute",
          data:
            dashboardData?.emissionScope3?.employeeCommuteEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(255, 68, 68, 1)",
          backgroundColor: "rgba(255, 68, 68, 0.6)",
          tension: 0.3,
          fill: true,
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope3?.wasteGeneratedEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Waste Generated from operations",
          data:
            dashboardData?.emissionScope3?.wasteGeneratedEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(72, 130, 87, 1)",
          backgroundColor: "rgba(72, 130, 87, 0.6)",
          tension: 0.5,
          fill: true,
          pointRadius: 0,
        }
      : "",
    dashboardData?.emissionScope3?.businessTravelEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? {
          label: "Business Travel",
          data:
            dashboardData?.emissionScope3?.businessTravelEmission?.map(
              (item) => item?.totalEmission
            ) ?? Array(generateMonthArray(fromDate, toDate)?.length).fill(0),
          borderColor: "rgba(146, 0, 183, 1)",
          backgroundColor: "rgba(146, 0, 183, 0.6)",
          tension: 0.4,
          fill: true,
          pointRadius: 0,
        }
      : "",
  ]?.filter(Boolean);

  let stationaryEmissionLabels =  (dashboardData?.moduleScope1?.naturalGas ||
    dashboardData?.moduleScope1?.coal ||
    dashboardData?.moduleScope1?.petrol || dashboardData?.moduleScope1?.diesel || dashboardData?.moduleScope1?.fuel ||
    dashboardData?.moduleScope1?.lpg || dashboardData?.moduleScope1?.propane) ?[
    {
      color: "#FFBB33",
      label: "Natural Gas",
      value: parseFloat(dashboardData?.moduleScope1?.naturalGas) || 0,
    },
    {
      color: "#000000",
      label: "Coal",
      value: parseFloat(dashboardData?.moduleScope1?.coal) || 0,
    },
    {
      color: "#0066E7",
      label: "Petrol",
      value: parseFloat(dashboardData?.moduleScope1?.petrol) || 0,
    },
    {
      color: "#FF4444",
      label: "Diesel",
      value: parseFloat(dashboardData?.moduleScope1?.diesel) || 0,
    },
    {
      color: "#28C76F",
      label: "Fuel Oil",
      value: parseFloat(dashboardData?.moduleScope1?.fuel) || 0,
    },
    {
      color: "#a16ae8",
      label: "LPG",
      value: parseFloat(dashboardData?.moduleScope1?.lpg) || 0,
    },
    {
      color: "#ff6b9d",
      label: "Propane",
      value: parseFloat(dashboardData?.moduleScope1?.propane) || 0,
    },
  ]:[];

  let mobileEmissionLabels =(dashboardData?.moduleScope1?.onRoad || dashboardData?.moduleScope1?.offRoad) ?  [
    {
      color: "#FFBB33",
      label: "On-Road",
      value: parseFloat(dashboardData?.moduleScope1?.onRoad) || 0,
    },
    {
      color: "#0066E7",
      label: "Off-Road",
      value: parseFloat(dashboardData?.moduleScope1?.offRoad) || 0,
    },
  ]:[];

  let fugitiveEmissionLabels =(dashboardData?.moduleScope1?.refrigeration ||dashboardData?.moduleScope1?.fireExtinguisher)?  [
    {
      color: "#0066E7",
      label: "Refrigeration",
      value: parseFloat(dashboardData?.moduleScope1?.refrigeration) || 0,
    },
    {
      color: "#FF4444",
      label: "Extinguisher",
      value: parseFloat(dashboardData?.moduleScope1?.fireExtinguisher) || 0,
    },
  ]:[];

  let scope2EmissionLabels = [
    {
      color: "#FF4444",
      label: "Electricity",
      value: parseFloat(dashboardData?.moduleScope2?.electricity) || 0,
    },
    {
      color: "#0066E7",
      label: "Heat",
      value: parseFloat(dashboardData?.moduleScope2?.heat) || 0,
    },
    {
      color: "#FFBB33",
      label: "Steam",
      value: parseFloat(dashboardData?.moduleScope2?.steam) || 0,
    },
  ];

  let scope3EmissionLabels = [
    {
      color: "rgba(255, 187, 51, 1)",
      label: "Purchased Goods and Services",
      value:
        parseFloat(dashboardData?.moduleScope3?.purchaseGoodsEmission) || 0,
    },
    {
      color: "rgba(0, 102, 231, 1)",
      label: "Transportation and Distribution",
      value:
        parseFloat(dashboardData?.moduleScope3?.transportationEmission) || 0,
    },
    {
      color: "rgba(255, 68, 68, 1)",
      label: "Employee Commute",
      value:
        parseFloat(dashboardData?.moduleScope3?.employeeCommuteEmission) || 0,
    },
    {
      color: "rgba(72, 130, 87, 1)",
      label: "Waste Generated",
      value:
        parseFloat(dashboardData?.moduleScope3?.wasteGeneratedEmission) || 0,
    },
    {
      color: "rgba(146, 0, 183, 1)",
      label: "Business Travel",
      value:
        parseFloat(dashboardData?.moduleScope3?.businessTravelEmission) || 0,
    },
  ];

  return (
    <>
      <p className={styles.card_heading}>Scope 1</p>

      <div className={styles.scope1_container}>
        <div className={styles.scope1_total}>
          <p className={styles.scope_heading}>
            Total GHG Emissions (tCO2e):{" "}
            <span className={styles.scope_value}>
              {formatNumberCommaSeparated(fixedTons(
                dashboardData?.emissionScope1?.totalEmission ?? 0)
              )}
            </span>
          </p>
          <LineChart
            dataset={scope1dataset}
            labels={generateMonthArray(fromDate, toDate)}
            yAxisTitle={"GHG Emissions (tCO2e)"}
            display={true}
            customHeight={"28rem"}
          />
        </div>
        <div className={styles.scope1_unique}>
          <div className={styles.scope1_single}>
            <p className={styles.scope_heading}>
              Total Stationary Emission (tCO2e)
            </p>
          {stationaryEmissionLabels?.length> 0 ? 
               <div className={styles.scope1_table}>
              <div className={styles.scope1_table_data}>
                {stationaryEmissionLabels.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: item.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_value}>
                      {formatNumberCommaSeparated(fixedTons(item?.value ?? 0))}
                    </span>
                  </div>
                ))}
              </div>

              <PieChart
                labels={stationaryEmissionLabels?.map((item) => item?.label)}
                dataset={generateDataset(stationaryEmissionLabels)}
                customWidth={100}
                customHeight={100}
                hide={true}
              />
            </div>
             :<NoDataFound/>}
          </div>
          <div className={styles.scope1_single}>
            <p className={styles.scope_heading}>
              Total Emission for Mobile Combustion (tCO2e)
            </p>
             {mobileEmissionLabels?.length>0 ?  <div className={styles.scope1_table}>
              <div className={styles.scope1_table_data}>
                {mobileEmissionLabels.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: item.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_value}>
                      {formatNumberCommaSeparated(fixedTons(item?.value ?? 0))}
                    </span>
                  </div>
                ))}
              </div>

              <PieChart
                labels={mobileEmissionLabels?.map((item) => item?.label)}
                dataset={generateDataset(mobileEmissionLabels)}
                customWidth={100}
                customHeight={100}
                hide={true}
              />
            </div>
             :<NoDataFound/>}
          </div>
          <div className={styles.scope1_single}>
            <p className={styles.scope_heading}>
              Total Fugitive Emission (tCO2e)
            </p>
            {fugitiveEmissionLabels?.length>0 ?  <div className={styles.scope1_table}>
              <div className={styles.scope1_table_data}>
                {fugitiveEmissionLabels.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: item.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_value}>
                      {formatNumberCommaSeparated(fixedTons(item?.value ?? 0))}
                    </span>
                  </div>
                ))}
                
              </div>

              <PieChart
                labels={fugitiveEmissionLabels?.map((item) => item?.label)}
                dataset={generateDataset(fugitiveEmissionLabels)}
                customWidth={100}
                customHeight={100}
                hide={true}
              />
            </div>
             :<NoDataFound/>}
          </div>
        </div>
      </div>

      <p className={styles.card_heading}>Scope 2</p>

      <div className={styles.scope2_container}>
        <div className={styles.scope2_total}>
          <p className={styles.scope_heading}>
            Total Emission from Purchased Energy (tCO2e):{" "}
            <span className={styles.scope_value}>
              {formatNumberCommaSeparated(fixedTons(
                dashboardData?.emissionScope2?.totalEmission ?? 0)
              )}
            </span>
          </p>
          <LineChart
            dataset={scope2dataset}
            labels={generateMonthArray(fromDate, toDate)}
            yAxisTitle={"Energy Emission (in tCO2e)"}
            display={true}
          />
        </div>
        <div className={styles.scope2_emission_type}>
          <p className={styles.scope_heading}>
            Emissions by Energy Type (tCO2e)
          </p>
          {scope2EmissionLabels?.map((item) => item?.value)?.filter(Boolean)
            ?.length > 0 ? (
            <>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <EchartDoughnutChart
                  labels={["Electricity", "Heat", "Steam"]}
                  dataPoints={
                    scope2EmissionLabels?.map((item) => item?.value) ?? [
                      0, 0, 0,
                    ]
                  }
                  colors={["#FF4444", "#0066E7", "#FFBB33"]}
                  customWidth={"150px"}
                  legendPosition="top"
                />
              </div>
              <div className={styles.scope2_table_data}>
                {scope2EmissionLabels.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: item.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_value}>
                      {formatNumberCommaSeparated(fixedTons(item?.value ?? 0))}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <NoDataFound mt={"50%"} />
          )}
        </div>
      </div>

      <p className={styles.card_heading}>Scope 3</p>

      <div className={styles.scope3_container}>
        <div className={styles.scope3_total}>
          <p className={styles.scope_heading}>
            Total Emission from value chain (tCO2e):{" "}
            <span className={styles.scope_value}>
              {formatNumberCommaSeparated(fixedTons(
                dashboardData?.emissionScope3?.totalEmission ?? 0)
              )}
            </span>
          </p>
          <LineChart
            dataset={scope3dataset}
            labels={generateMonthArray(fromDate, toDate)}
            display={true}
          />
        </div>
        <div className={styles.scope3_emission_transport}>
          <p className={styles.scope_heading}>
            Total Emission from Transportation and Distribution (tCO2e)
          </p>
          {scope3EmissionLabels.map((item) => item?.value)?.filter(Boolean)
            ?.length > 0 ? (
            <>
              {" "}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <EchartDoughnutChart
                  labels={scope3EmissionLabels.map((item) => item.label)}
                  dataPoints={scope3EmissionLabels.map((item) => item.value)}
                  colors={scope3EmissionLabels.map((item) => item.color)}
                  customWidth={"150px"}
                  legendPosition="top"
                />
              </div>
              <div className={styles.scope2_table_data}>
                {scope3EmissionLabels.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: item.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_value}>
                      {formatNumberCommaSeparated(fixedTons(item?.value ?? 0))}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <NoDataFound mt={"30%"}/>
          )}
        </div>
      </div>
    </>
  );
};

export default Upstream;
